/**
 * Plan Terms Sub Table
 *
 * Rendered inside an expanded subscription-plan row. Lists the plan's
 * billing terms and exposes an "Update" action per term.
 */

import { Pencil } from 'lucide-react';

import { Badge, Button } from '@/components/common';
import type { SubscriptionPlan, SubscriptionPlanTermGrid } from '@/api/types';

interface PlanTermsSubTableProps {
  plan: SubscriptionPlan;
  /** Whether the current user can update plan terms (ROLE_SADMIN only) */
  canManageTerms?: boolean;
  onEditTerm: (term: SubscriptionPlanTermGrid) => void;
}

function formatDuration(term: SubscriptionPlanTermGrid): string {
  const unit = term.termType?.toUpperCase() === 'YEAR' ? 'Year' : 'Month';
  const plural = term.termValue > 1 ? 's' : '';
  return `${term.termValue} ${unit}${plural}`;
}

export function PlanTermsSubTable({
  plan,
  canManageTerms = false,
  onEditTerm,
}: PlanTermsSubTableProps) {
  const terms = plan.planTerms ?? [];

  if (terms.length === 0) {
    return (
      <p className="px-2 py-3 text-sm text-foreground-muted dark:text-foreground-dark-muted">
        No terms configured for this plan.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wider text-foreground-muted dark:text-foreground-dark-muted">
        Plan Terms
      </p>
      <div className="overflow-x-auto rounded-md border border-border dark:border-border-dark">
        <table className="w-full border-collapse text-sm">
          <thead className="bg-secondary-100/60 dark:bg-secondary-800/40">
            <tr className="text-left text-xs font-semibold uppercase tracking-wider text-foreground-muted dark:text-foreground-dark-muted">
              <th className="px-4 py-2">Term Code</th>
              <th className="px-4 py-2">Duration</th>
              <th className="px-4 py-2">Price</th>
              <th className="px-4 py-2">Actual Price</th>
              <th className="px-4 py-2">Status</th>
              {canManageTerms && (
                <th className="px-4 py-2 text-right">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {terms.map((term) => (
              <tr
                key={term.id}
                className="border-t border-border dark:border-border-dark"
              >
                <td className="px-4 py-2 font-medium text-foreground dark:text-foreground-dark">
                  {term.planTermCode}
                </td>
                <td className="px-4 py-2 text-foreground dark:text-foreground-dark">
                  {formatDuration(term)}
                </td>
                <td className="px-4 py-2 text-foreground dark:text-foreground-dark">
                  {term.price?.toLocaleString()}
                </td>
                <td className="px-4 py-2 text-foreground-muted dark:text-foreground-dark-muted">
                  {term.actualPrice?.toLocaleString()}
                </td>
                <td className="px-4 py-2">
                  <Badge variant={term.isActive ? 'success' : 'default'} dot>
                    {term.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                {canManageTerms && (
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<Pencil className="h-4 w-4" />}
                      onClick={() => onEditTerm(term)}
                    >
                      Update
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default PlanTermsSubTable;
